/**
 * The automation handle.
 *
 * Exposed deliberately: it is what the console and the end-to-end tests reach
 * for. Read-only accessors plus a few helpers for locating 3D parts on screen
 * — no behaviour lives here.
 */

import { DIM, FOCUS } from '../geometry/dims.ts';
import { fmtTime } from '../lib/format.ts';
import { lib } from '../state/library.ts';
import { deck } from '../state/player.ts';
import { prefsSnapshot } from '../state/prefs.ts';
import * as ui from '../state/ui.ts';
import type { HitType } from '../three/parts/hits.ts';
import { safeInsets } from '../three/views.ts';
import { hardware } from './hardware.ts';
import { pickAt, type ScreenPoint, screenOf, screenOfWorld } from './picking.ts';

export interface Box {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface DeckBounds extends Box {
  viewport: { width: number; height: number };
}

/** A flat snapshot of everything a test might want to assert on. */
function snapshot() {
  const { stage, tt } = hardware();
  const track = deck.track;
  return {
    ready: ui.ready(),
    track: track ? { id: track.id, title: track.title, source: track.source } : null,
    index: deck.index,
    queue: deck.queue.length,
    playing: deck.playing,
    position: deck.position,
    duration: deck.duration,
    progress: deck.progress,
    volume: deck.volume,
    muted: deck.muted,
    shuffle: deck.playlist.shuffle,
    repeat: deck.playlist.repeat,
    rpm: tt.state.rpm,
    pitch: tt.state.pitch,
    spin: tt.state.spin,
    armYaw: tt.state.armYaw,
    armTargetYaw: tt.state.armTargetYaw,
    armDown: tt.state.armDown,
    armFollow: tt.state.armFollow,
    powered: tt.state.powered,
    seated: tt.state.seated,
    platterAngle: tt.platterAngle,
    view: stage.state.view,
    layoutYaw: stage.state.layoutYaw,
    cameraPos: stage.camera.position.toArray().map((n) => Number(n.toFixed(3))),
    sheetOpen: ui.sheet().open,
    sheetView: ui.sheet().view,
    modalOpen: ui.modal() !== null,
    menuOpen: ui.menuOpen(),
    fullscreen: ui.fullscreen(),
    fullscreenOffer: ui.fullscreenOffer(),
    accent: getComputedStyle(document.documentElement).getPropertyValue('--accent').trim(),
  };
}

/** The screen box round a set of deck-space points. */
function boxOf(points: readonly (readonly [number, number, number])[]): Box {
  const pts = points.map(([x, y, z]) => screenOfWorld(x, y, z));
  return {
    left: Math.min(...pts.map((p) => p.x)),
    right: Math.max(...pts.map((p) => p.x)),
    top: Math.min(...pts.map((p) => p.y)),
    bottom: Math.max(...pts.map((p) => p.y)),
  };
}

const viewport = (): { width: number; height: number } => {
  const r = hardware().stage.renderer.domElement.getBoundingClientRect();
  return { width: r.width, height: r.height };
};

/**
 * Screen-space box of the plinth itself — used to check the deck is framed.
 * Measured from the chassis, not from the deck root, whose bounds also include
 * the soft contact shadow that deliberately spills well past the deck.
 */
function deckBounds(): DeckBounds {
  const hw = DIM.deckW / 2;
  const hd = DIM.deckD / 2;
  const corners: [number, number, number][] = [];
  for (const x of [-hw, hw]) {
    for (const y of [0, DIM.top]) {
      for (const z of [-hd, hd]) corners.push([x, y, z]);
    }
  }
  return { ...boxOf(corners), viewport: viewport() };
}

/** Screen-space box of the focus region: the record, the tonearm and the buttons in front. */
function focusBounds(): DeckBounds {
  const corners: [number, number, number][] = [];
  for (const x of [FOCUS.minX, FOCUS.maxX]) {
    for (const z of [FOCUS.minZ, FOCUS.maxZ]) corners.push([x, FOCUS.y, z]);
  }
  return { ...boxOf(corners), viewport: viewport() };
}

/** The band of the viewport the glass chrome leaves clear, in CSS pixels from the top. */
function safeBand(): { top: number; bottom: number } {
  const canvas = hardware().stage.renderer.domElement;
  const ins = safeInsets(canvas);
  return { top: ins.top, bottom: canvas.clientHeight - ins.bottom };
}

const handle = {
  get ready() {
    return ui.ready();
  },
  get deck() {
    return deck;
  },
  get tt() {
    return hardware().tt;
  },
  get stage() {
    return hardware().stage;
  },
  get lib() {
    return lib;
  },
  get prefs() {
    return prefsSnapshot();
  },
  fmtTime,
  snapshot,
  deckBounds,
  focusBounds,
  safeBand,
  screenOfWorld,

  /**
   * Screen position of an interactive deck part, by its hit type. A `value`
   * picks between parts that share a type, such as the 33 and 45 buttons.
   */
  screenOfPart: (type: HitType, value?: number): ScreenPoint | null =>
    screenOf(hardware().tt.findHit(type, value)),

  /** Which deck part, if any, a pointer at this screen point would grab. */
  partUnder: (x: number, y: number): HitType | null => pickAt(x, y)?.hit.type ?? null,

  /** Screen position of a point on the record, at `radius` cm and `angle` rad. */
  screenOfDisc: (radius = 10, angle = 0): ScreenPoint =>
    screenOfWorld(
      DIM.platter.x + Math.cos(angle) * radius,
      DIM.recordTop,
      DIM.platter.z + Math.sin(angle) * radius,
    ),
};

export type DebugHandle = typeof handle;

declare global {
  interface Window {
    turntable: DebugHandle;
  }
}

export function installDebugHandle(): void {
  window.turntable = handle;
  document.documentElement.dataset.ready = 'true';
}
